/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2023, Zumper
 * @description PinMarker
 */

import { Marker } from '@vis.gl/react-mapbox'
import { Popover, PopoverContent, PopoverTrigger } from '@zcode-ui/shadz-ui'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import React, { type ForwardedRef, type MouseEvent } from 'react'
import { usePinMarkerContext } from '../../../pages/PinMarkerContext'
import type { MixpanelMaplistClickType } from '../../../types'
import { PinMarkerGroupPreview } from './PinMarkerGroupPreview/PinMarkerGroupPreview'
import { PinMarkerListingPreview } from './PinMarkerListingPreview'
import { PinWithInfo } from './PinWithInfo'

export interface PinMarkerDesktopProps {
  handlePinClick: () => void
  hovered: boolean
  isActive: boolean
  isDot: boolean
  isSpotlight?: boolean
  isVisited: boolean
  lat?: number
  lng?: number
  markerRef: ForwardedRef<HTMLDivElement>
  mixpanelMaplistClick: ({
    buttonClicked,
    entity,
    pageName,
    section,
  }: MixpanelMaplistClickType) => void
  onClose: () => void
  onMouseOut: () => void
  onMouseOver: () => void
  previewListable:
    | { pin: Listable | Pin<RawPinInventorySearch>; showPriceMarker: boolean }
    | ReadonlyArray<{
        pin: Listable | Pin<RawPinInventorySearch>
        showPriceMarker: boolean
      }>
  priceTxt: string
}

export const PinMarkerDesktop = ({
  handlePinClick,
  hovered,
  isActive,
  isDot,
  isSpotlight,
  isVisited,
  lat,
  lng,
  markerRef,
  mixpanelMaplistClick,
  onClose,
  onMouseOut,
  onMouseOver,
  previewListable,
  priceTxt,
}: PinMarkerDesktopProps) => {
  const { mapRef } = usePinMarkerContext()
  const isGroup = Array.isArray(previewListable)
  if (!lat || !lng) {
    return null
  }

  return (
    <Marker
      anchor={isDot ? undefined : 'bottom'}
      draggable={false}
      latitude={lat}
      longitude={lng}
      style={{
        zIndex: isActive ? 5 : hovered ? 4 : isSpotlight ? 3 : !isDot ? 2 : 1,
        cursor: 'pointer',
      }}
    >
      <div
        className={`before:absolute before:h-[calc(100%+8px)] before:w-[calc(100%+8px)] before:left-1/2 before:top-1/2 ${
          isDot
            ? 'h-[14px] before:translate-x-[-50%] before:translate-y-[-50%]'
            : 'before:translate-x-[-50%] before:translate-y-[-60%]'
        }`}
        onBlur={() => {}}
        onClick={handlePinClick}
        onFocus={onMouseOver}
        onMouseOut={onMouseOut}
        onMouseOver={onMouseOver}
        ref={markerRef}
        role="presentation"
        onKeyDown={handlePinClick}
      >
        <Popover
          open={isActive}
          onOpenChange={(open) => {
            !open && onClose()
          }}
        >
          <PopoverTrigger aria-expanded={isActive} aria-label="pin-marker-desktop">
            {isDot ? (
              <div
                className={`h-[14px] w-[14px] cursor-pointer rounded-full border border-solid ${
                  isActive
                    ? 'bg-gray-0 border-pink-600 border-2'
                    : hovered
                      ? 'bg-gray-0 border-neutral-650'
                      : isVisited
                        ? 'bg-neutral-650 border-gray-0'
                        : 'bg-blue-800 border-gray-0'
                }`}
                data-testid="dot-marker"
              />
            ) : (
              <PinWithInfo
                hovered={hovered}
                isActive={isActive}
                isSpotlight={isSpotlight}
                isVisited={isVisited}
                priceTxt={priceTxt}
              />
            )}
          </PopoverTrigger>
          {isActive && (
            <PopoverContent
              collisionBoundary={mapRef ? [mapRef.current] : []}
              className={`border-none mx-5 p-0 rounded-xl focus:outline-none focus:ring-0 ${isGroup ? 'w-[300px] h-[346px] shadow-[0px_8px_16px_rgba(0,0,0,0.12)] rounded-lg overflow-hidden' : 'w-[256px] h-[355px]'}`}
              onOpenAutoFocus={(e) => e.preventDefault()}
              side="top"
              sideOffset={8}
              onClick={(e: MouseEvent<HTMLDivElement>) => {
                e.stopPropagation()
              }}
            >
              {isGroup ? (
                <PinMarkerGroupPreview
                  onClose={onClose}
                  mixpanelMaplistClick={mixpanelMaplistClick}
                  previewListable={previewListable}
                />
              ) : (
                <PinMarkerListingPreview
                  listable={previewListable.pin}
                  mixpanelMaplistClick={mixpanelMaplistClick}
                  onClose={onClose}
                />
              )}
            </PopoverContent>
          )}
        </Popover>
      </div>
    </Marker>
  )
}
